import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import Nav from "@/components/Nav";
import CurrencyInput from "@/components/CurrencyInput";
import SchoolSearch from "@/components/netprice/SchoolSearch";
import ProgressDots from "@/components/netprice/ProgressDots";
import QuestionStep from "@/components/netprice/QuestionStep";
import NetPriceResult from "@/components/netprice/NetPriceResult";
import LiveAINetPricePanel from "@/components/netprice/LiveAINetPricePanel";
import ResidencyToggle from "@/components/ResidencyToggle";
import {
  HOME_EQUITY_OPTIONS,
  RETIREMENT_OPTIONS,
  getBracketKeyFromIncome,
  assetAdjustment,
} from "@/lib/netprice-brackets";
import { track } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOTAL_STEPS = 7;

const FAMILY_SIZE_OPTIONS = Array.from({ length: 8 }, (_, i) => ({
  value: String(i + 1),
  label: `${i + 1}${i === 7 ? " or more" : ""} ${i === 0 ? "person" : "people"}`,
}));

const STUDENTS_IN_COLLEGE_OPTIONS = [
  { value: "1", label: "1 (just me)" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4 or more" },
];

/**
 * /netprice — 6-question Net Price Calculator.
 *
 * State machine:
 *   step 1: pick a school
 *   step 2: family income
 *   step 3: home equity
 *   step 4: savings + investments
 *   step 5: retirement savings
 *   step 6: family size
 *   step 7: result
 *
 * Once the user has answered Q2-Q6 once, adding another school skips
 * straight to step 1 -> result with the same answers reused.
 */
export default function NetPricePage() {
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({
    income: "", homeEquity: "", savings: "", retirement: "", familySize: "", studentsInCollege: "", residency: "",
  });
  const [pendingSchool, setPendingSchool] = useState(null);
  const [school, setSchool] = useState(null);
  const [schoolLoading, setSchoolLoading] = useState(false);
  const [schoolError, setSchoolError] = useState(null);
  const [comparison, setComparison] = useState([]); // [{ school, answers }, ...]

  useEffect(() => { track.tool_opened("netprice"); }, []);

  const incomeAnswered = answers.income !== "";

  // Public ownership === 1, residency matters. Private (2/3) — same
  // tuition for everyone so we auto-pick in_state silently and disable
  // the toggle with an explanatory note.
  const isPublic = (s) => s && (s.ownership === 1 || s.type === "Public");

  // Stage 1: load detail + show residency toggle without advancing yet.
  const fetchSchool = async (s) => {
    setSchoolLoading(true);
    setSchoolError(null);
    try {
      const r = await fetch(`${API}/netprice/${s.id}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setPendingSchool(j);
      // Reset residency on every new school. Private schools auto-set
      // to in_state behind the scenes so the user can advance.
      setAnswers(p => ({ ...p, residency: isPublic(j) ? "" : "in_state" }));
    } catch (e) {
      setSchoolError("Could not load school data. Try another school or refresh.");
    } finally {
      setSchoolLoading(false);
    }
  };

  // Stage 2: residency picked, commit pendingSchool and advance.
  const commitSchool = () => {
    if (!pendingSchool) return;
    if (!answers.residency) return;
    setSchool(pendingSchool);
    if (school) {
      if (incomeAnswered && answers.familySize && answers.studentsInCollege) {
        setStep(8);
      } else if (incomeAnswered && answers.familySize) {
        setStep(7);
      } else {
        setStep(2);
      }
    } else {
      setStep(2);
    }
  };

  const setAns = (key, val, nextStep) => {
    setAnswers(p => ({ ...p, [key]: val }));
    setStep(nextStep);
  };

  const startCompare = () => {
    if (school) setComparison(prev => {
      if (prev.find(x => x.school.id === school.id)) return prev;
      const nextItem = { school, answers: { ...answers } };
      return [...prev, nextItem].slice(-4);
    });
    setSchool(null);
    setPendingSchool(null);
    setAnswers(p => ({ ...p, residency: "", studentsInCollege: "" }));
    setStep(1);
  };

  const back = () => {
    if (step === 1) return;
    setStep(s => Math.max(1, s - 1));
  };

  return (
    <div className="theme-light min-h-screen flex flex-col bg-white">
      <Nav />

      <main className="flex-1 bg-white">
        <div className="max-w-3xl mx-auto w-full px-4 sm:px-6 py-10 sm:py-14">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm mb-7 text-gray-500 hover:text-gray-900 transition-colors"
            data-testid="netprice-back">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to AidWaive
          </Link>

          <div className="flex items-baseline justify-between gap-4 flex-wrap mb-8">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] mb-3 text-gray-400">
                Live Net Price Estimator · Powered by AI
              </div>
              <h1 className="font-heading leading-[1.05] text-gray-900"
                style={{ fontSize: "clamp(2.2rem, 4vw, 3.2rem)", letterSpacing: "-0.022em" }}>
                What will it actually <em style={{ color: "#1d4ed8" }}>cost?</em>
              </h1>
              <p className="mt-4 text-base sm:text-lg leading-relaxed text-gray-500 max-w-2xl">
                Seven questions, sixty seconds. We reason live over your inputs, the College Scorecard, and each school's published aid policy. Every number comes with sources, a confidence label, and a freshness timestamp.
              </p>
            </div>
            {step <= 7 && <ProgressDots total={TOTAL_STEPS} current={step} />}
          </div>

          {/* STEP 1: school */}
          {step === 1 && (
            <div className="fade-in" data-testid="netprice-step-1">
              <h2 className="font-heading mb-4 leading-[1.1]"
                style={{ color: "#111928", fontSize: "clamp(1.6rem, 2.6vw, 2rem)" }}>
                Which school are you looking at?
              </h2>
              <p className="text-sm mb-6" style={{ color: "#6b7280" }}>
                Start with one school. You can add up to four after seeing the result.
              </p>
              <SchoolSearch onSelect={fetchSchool} />
              {schoolLoading && (
                <div className="mt-5 text-sm inline-flex items-center gap-2"
                  style={{ color: "#6b7280" }} data-testid="netprice-school-loading">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading school data
                </div>
              )}
              {schoolError && (
                <div className="mt-5 text-sm" style={{ color: "#b91c1c" }} data-testid="netprice-school-error">
                  {schoolError}
                </div>
              )}
              {pendingSchool && !schoolLoading && (
                <div className="mt-6 rounded-xl border border-[#bfdbfe] bg-[#eff6ff] p-4 sm:p-5 fade-in" data-testid="netprice-school-pending">
                  <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
                    <div className="font-medium text-gray-900 text-base">{pendingSchool.name}</div>
                    <button type="button" onClick={() => { setPendingSchool(null); setAnswers(p => ({ ...p, residency: "" })); }}
                      className="text-xs underline text-[#1d4ed8]" data-testid="netprice-school-pending-change">
                      Change school
                    </button>
                  </div>
                  <ResidencyToggle
                    value={answers.residency}
                    onChange={(v) => setAnswers(p => ({ ...p, residency: v }))}
                    disabled={!isPublic(pendingSchool)}
                    disabledNote="Private schools charge the same tuition for all students."
                    testId="netprice-residency"
                  />
                  <button
                    type="button"
                    onClick={commitSchool}
                    disabled={!answers.residency}
                    className="mt-4 inline-flex items-center gap-2 px-5 h-10 rounded-md text-sm font-medium disabled:opacity-50 hover:opacity-90"
                    style={{ background: "#1d4ed8", color: "#ffffff" }}
                    data-testid="netprice-residency-continue">
                    Continue <ArrowRight className="w-4 h-4" />
                  </button>
                  {!answers.residency && (
                    <p className="text-xs text-amber-700 mt-2" data-testid="netprice-residency-required">
                      Pick In-State or Out-of-State to continue.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <QuestionStep
              testid="netprice-step-2"
              index={2} total={TOTAL_STEPS}
              question="What is your family's total income before taxes?"
              hint="Type the exact number, a rough estimate, or your best guess. We use this to match a federal income bracket and calculate your aid."
              onBack={back}
              showPrivacy
              custom={
                <CurrencyAnswer
                  testid="netprice-income"
                  value={answers.income}
                  onChange={(v) => setAnswers(p => ({ ...p, income: v }))}
                  onContinue={() => setStep(3)}
                  placeholder="85,000"
                  ariaLabel="Family income before taxes"
                />
              }
            />
          )}

          {step === 3 && (
            <QuestionStep
              testid="netprice-step-3"
              index={3} total={TOTAL_STEPS}
              question="Roughly how much equity do you have in your home?"
              hint="Home value minus what you still owe on the mortgage."
              options={HOME_EQUITY_OPTIONS}
              onSelect={(o) => setAns("homeEquity", o.value, 4)}
              onBack={back}
            />
          )}

          {step === 4 && (
            <QuestionStep
              testid="netprice-step-4"
              index={4} total={TOTAL_STEPS}
              question="How much do you have in savings, checking, and investments?"
              hint="Not including retirement accounts. 401k and IRA do not count here."
              onBack={back}
              custom={
                <CurrencyAnswer
                  testid="netprice-savings"
                  value={answers.savings}
                  onChange={(v) => setAnswers(p => ({ ...p, savings: v }))}
                  onContinue={() => setStep(5)}
                  placeholder="25,000"
                  ariaLabel="Total savings, checking, and investments"
                />
              }
            />
          )}

          {step === 5 && (
            <QuestionStep
              testid="netprice-step-5"
              index={5} total={TOTAL_STEPS}
              question="Roughly how much in retirement accounts?"
              hint="401k, IRA, pension. These are excluded from the FAFSA calculation, so this answer does not affect the estimate. We ask only to keep the picture honest."
              options={RETIREMENT_OPTIONS}
              onSelect={(o) => setAns("retirement", o.value, 6)}
              onBack={back}
            />
          )}

          {step === 6 && (
            <QuestionStep
              testid="netprice-step-6"
              index={6} total={TOTAL_STEPS}
              question="How many people are in your household?"
              hint="Include yourself, parents, and any siblings supported by the family."
              options={FAMILY_SIZE_OPTIONS}
              onSelect={(o) => setAns("familySize", o.value, 7)}
              onBack={back}
            />
          )}

          {step === 7 && (
            <QuestionStep
              testid="netprice-step-7"
              index={7} total={TOTAL_STEPS}
              question="How many family members are currently in college?"
              hint="As of 2024-2025, FAFSA evaluates each student independently, so this matters for your final out-of-pocket cost."
              options={STUDENTS_IN_COLLEGE_OPTIONS}
              onSelect={(o) => setAns("studentsInCollege", o.value, 8)}
              onBack={back}
            />
          )}

          {step === 8 && school && (
            <div className="mt-2">
              <LiveAINetPricePanel school={school} answers={answers} />
              <NetPriceResult
                school={school}
                answers={answers}
                onCompareAnother={startCompare}
              />
              {comparison.length > 0 && (
                <CompareTable
                  rows={[
                    ...comparison,
                    { school, answers },
                  ]}
                />
              )}
            </div>
          )}
        </div>
      </main>

      <footer className="border-t mt-16" style={{ borderColor: "#e5e7eb", background: "#ffffff" }}>
        <div className="max-w-3xl mx-auto w-full px-4 sm:px-6 py-8 text-xs flex justify-between"
          style={{ color: "#9ca3af" }}>
          <Link to="/" className="hover:underline">All AidWaive tools</Link>
          <div className="flex gap-5">
            <Link to="/journey" className="hover:underline">The Journey</Link>
            <Link to="/legal/privacy" className="hover:underline">Privacy</Link>
            <Link to="/legal/terms" className="hover:underline">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

/**
 * Side-by-side comparison row table for up to 4 schools. Highlights the
 * lowest net price as best value.
 */
function CompareTable({ rows }) {
  const computed = rows.map(({ school, answers }) => {
    const b = getBracketKeyFromIncome(answers.income);
    const base = school.netPriceByIncome?.[b.apiKey];
    const adj = assetAdjustment(answers.homeEquity, answers.savings);
    const np = base != null ? Math.round(base + adj / 2) : null; // mid of range
    const sticker = school.costOfAttendance || school.tuitionOutOfState || 0;
    return { school, np, sticker, save: np && sticker ? Math.max(0, sticker - np) : null };
  });
  const minIdx = computed.reduce((acc, c, i) =>
    (c.np != null && (acc === -1 || c.np < computed[acc].np)) ? i : acc, -1);

  return (
    <div className="mt-12" data-testid="netprice-compare-table">
      <h2 className="font-heading text-2xl sm:text-3xl mb-5" style={{ color: "#111928" }}>
        Side by side
      </h2>
      <div className="rounded-xl overflow-x-auto" style={{ background: "#ffffff", border: "1px solid #e5e7eb" }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
              <th className="text-left p-4 font-medium" style={{ color: "#9ca3af" }}>School</th>
              <th className="text-right p-4 font-medium" style={{ color: "#9ca3af" }}>Net price</th>
              <th className="text-right p-4 font-medium" style={{ color: "#9ca3af" }}>Sticker</th>
              <th className="text-right p-4 font-medium" style={{ color: "#9ca3af" }}>You save</th>
              <th className="text-right p-4 font-medium" style={{ color: "#9ca3af" }}>Grad rate</th>
            </tr>
          </thead>
          <tbody>
            {computed.map((c, i) => (
              <tr key={c.school.id} style={{ borderBottom: i < computed.length - 1 ? "1px solid #f3f4f6" : "none", background: i === minIdx ? "#f0fdf4" : "transparent" }}>
                <td className="p-4" style={{ color: "#111928", fontWeight: 500 }}>
                  {c.school.name}
                  {i === minIdx && (
                    <span className="ml-2 text-[11px] uppercase tracking-[0.12em] font-medium" style={{ color: "#15803d" }}>
                      best value
                    </span>
                  )}
                </td>
                <td className="p-4 text-right tabular-nums" style={{ color: i === minIdx ? "#15803d" : "#111928", fontWeight: i === minIdx ? 600 : 400 }}>
                  {c.np != null ? `$${c.np.toLocaleString()}` : "n/a"}
                </td>
                <td className="p-4 text-right tabular-nums" style={{ color: "#6b7280" }}>
                  {c.sticker ? `$${c.sticker.toLocaleString()}` : "n/a"}
                </td>
                <td className="p-4 text-right tabular-nums" style={{ color: "#6b7280" }}>
                  {c.save != null ? `$${c.save.toLocaleString()}` : "n/a"}
                </td>
                <td className="p-4 text-right tabular-nums" style={{ color: "#6b7280" }}>
                  {typeof c.school.graduationRate === "number"
                    ? `${Math.round(c.school.graduationRate * 100)}%`
                    : "n/a"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/**
 * CurrencyAnswer — full-width currency input + Continue button that fits
 * the QuestionStep `custom` slot. Disables Continue until the user types
 * a value greater than zero. Pressing Enter advances the same way.
 */
function CurrencyAnswer({ value, onChange, onContinue, placeholder, testid, ariaLabel }) {
  const n = Number(String(value || "").replace(/[^0-9]/g, ""));
  const valid = n > 0;
  const onKeyDown = (e) => {
    if (e.key === "Enter" && valid) onContinue();
  };
  return (
    <div className="max-w-xl" onKeyDown={onKeyDown} data-testid={`${testid}-wrap`}>
      <CurrencyInput
        id={testid}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        testid={testid}
      />
      <p className="text-xs mt-2" style={{ color: "#9ca3af" }} aria-label={ariaLabel}>
        A rough number is fine. We use this to match a federal income bracket and adjust for assets.
      </p>
      <button
        type="button"
        onClick={onContinue}
        disabled={!valid}
        className="mt-5 inline-flex items-center gap-2 px-5 h-11 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-opacity hover:opacity-90"
        style={{ background: "#1d4ed8", color: "#ffffff" }}
        data-testid={`${testid}-continue`}>
        Continue <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}
